import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { mat } from "./WorldModels";

const ROAD_ROOT = "/assets/kenney-roads";
const ROAD_TINT = 0xe8e0d4;

export interface RoadPart {
  geometry: THREE.BufferGeometry;
  material: THREE.MeshLambertMaterial;
  matrix: THREE.Matrix4;
}

export interface RoadTemplate {
  id: string;
  parts: RoadPart[];
  size: number;
}

const ROAD_FILES = [
  "road-straight.glb",
  "road-crossroad.glb",
  "road-intersection.glb",
  "road-bend.glb",
  "road-curve.glb",
  "road-roundabout.glb",
  "light-curved.glb",
  "light-square.glb",
  // Sidewalk / path tiles for outer belt accents.
  "road-bend-sidewalk.glb",
  "road-curve-pavement.glb",
  "road-side.glb",
  "road-crossroad-path.glb",
  "road-intersection-path.glb",
  "tile-low.glb",
] as const;

let ready = false;
let loadPromise: Promise<void> | null = null;
const catalog = new Map<string, RoadTemplate>();
const mappedMatCache = new Map<string, THREE.MeshLambertMaterial>();

function matMapped(color: number, map: THREE.Texture | null): THREE.MeshLambertMaterial {
  if (!map) {
    return mat(color);
  }
  const key = `${color}:${map.uuid}`;
  let m = mappedMatCache.get(key);
  if (!m) {
    m = new THREE.MeshLambertMaterial({ color, map });
    m.userData.shared = true;
    mappedMatCache.set(key, m);
  }
  return m;
}

export function loadRoadAssets(): Promise<void> {
  if (ready) {
    return Promise.resolve();
  }
  if (loadPromise) {
    return loadPromise;
  }
  loadPromise = (async () => {
    const loader = new GLTFLoader();
    await Promise.all(ROAD_FILES.map((f) => loadOne(loader, f)));
    ready = true;
  })();
  return loadPromise;
}

async function loadOne(loader: GLTFLoader, file: string): Promise<void> {
  try {
    const gltf = await loader.loadAsync(`${ROAD_ROOT}/${file}`);
    const root = gltf.scene;
    root.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(root);
    if (Number.isFinite(box.min.y)) {
      root.position.y -= box.min.y;
      root.updateMatrixWorld(true);
    }
    const sized = new THREE.Box3().setFromObject(root);
    const span = Math.max(
      sized.max.x - sized.min.x,
      sized.max.z - sized.min.z,
      0.5,
    );
    // City Kit roads are ~1u tiles; scale to ~6u to match our belt thickness.
    const target = file.includes("light") ? 2.2 : 6.2;
    const fit = target / span;
    root.scale.setScalar(fit);
    root.updateMatrixWorld(true);
    const fitted = new THREE.Box3().setFromObject(root);

    const parts: RoadPart[] = [];
    root.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh) || !obj.geometry) {
        return;
      }
      const srcMat = Array.isArray(obj.material) ? obj.material[0] : obj.material;
      const map =
        srcMat && "map" in srcMat
          ? ((srcMat as THREE.MeshStandardMaterial).map ?? null)
          : null;
      if (map) {
        map.colorSpace = THREE.SRGBColorSpace;
        map.needsUpdate = true;
      }
      const geometry = obj.geometry.clone();
      geometry.userData.shared = true;
      parts.push({
        geometry,
        material: matMapped(map ? ROAD_TINT : 0x8a847c, map),
        matrix: obj.matrixWorld.clone(),
      });
    });
    if (parts.length === 0) {
      return;
    }
    const id = file.replace(/\.glb$/i, "");
    catalog.set(id, {
      id,
      parts,
      size: Math.max(fitted.max.x - fitted.min.x, fitted.max.z - fitted.min.z),
    });
  } catch (err) {
    console.warn(`[roads] failed to load ${file}`, err);
  }
}

export function cloneRoad(id: string): THREE.Group | null {
  const template = catalog.get(id);
  if (!template) {
    return null;
  }
  const group = new THREE.Group();
  group.name = `road-${template.id}`;
  for (const part of template.parts) {
    const mesh = new THREE.Mesh(part.geometry, part.material);
    mesh.matrix.copy(part.matrix);
    mesh.matrixAutoUpdate = false;
    group.add(mesh);
  }
  return group;
}
